import { SUPPORTED_LOCALES } from '../i18n/config';
import {
  gaisukeFaqPath,
  gaisukePublicPath,
  gaisukeWeeklySummaryPath,
  isGaisukeFaqPath,
  isGaisukePublicPath,
  isValidGaisukeWeekStart,
} from './gaisukePath';
import { isKeibaArenaPublicPath, keibaArenaPublicPath } from './keibaArenaPath';
import { banquetPublicPath, isBanquetPublicPath } from './banquetPath';
import {
  econReportDetailPath,
  econReportPublicPath,
  isEconReportPublicPath,
  parseEconReportSlugFromPath,
} from './econReportPath';
import { gaisukeFxPublicPath, isGaisukeFxPublicPath } from './gaisukeFxPath';

/** 公開ページの locale 付きパス（例: /help, /en/gaisuke）。ja は help/contact も無印。 */
export function localePrefixedPath(locale: string, segment: string): string {
  if (segment === 'gaisuke') {
    return gaisukePublicPath(locale);
  }
  if (segment === 'arena/keiba' || segment === 'keiba-arena') {
    return keibaArenaPublicPath(locale);
  }
  if (segment === 'arena/banquet') {
    return banquetPublicPath(locale);
  }
  if (segment === 'arena/econ') {
    return econReportPublicPath(locale);
  }
  if (segment === 'arena/fx') {
    return gaisukeFxPublicPath(locale);
  }
  // help / contact: `/` 同様、ja 正規は無印（/ja/help は重複 URL）
  if (
    (segment === 'help' || segment === 'contact') &&
    (locale === 'ja' || locale.startsWith('ja-'))
  ) {
    return `/${segment}`;
  }
  return `/${locale}/${segment}`;
}

/** /privacy, /terms, /help, /contact 等の locale 付きパス判定 */
export function isLocalePrefixedPage(pathname: string, page: string): boolean {
  if (pathname === `/${page}`) return true;
  return SUPPORTED_LOCALES.some((loc) => pathname === `/${loc}/${page}`);
}

export function isHelpPath(pathname: string): boolean {
  return isLocalePrefixedPage(pathname, 'help');
}

export function isContactPath(pathname: string): boolean {
  return isLocalePrefixedPage(pathname, 'contact');
}

/** 言語切り替え時に /:locale/{page} へ遷移する公開ページ */
export const LOCALE_SWITCH_PUBLIC_PAGES = [
  'help',
  'contact',
  'gaisuke',
  'arena/keiba',
  'arena/banquet',
  'arena/econ',
  'arena/fx',
] as const;

/** 言語切替のパスを専用ヘルパーで組むページ（下の for ループでは扱わない） */
const LOCALE_SWITCH_CUSTOM_PAGES: readonly string[] = [
  'gaisuke',
  'arena/keiba',
  'arena/banquet',
  'arena/econ',
  'arena/fx',
];

/** pathname から週次サマリーの weekStart を取る（例: /en/gaisuke/weekly/2026-05-30） */
export function parseGaisukeWeeklyWeekStartFromPath(pathname: string): string | null {
  const m = pathname.match(/\/gaisuke\/weekly\/(\d{4}-\d{2}-\d{2})\/?$/);
  if (!m?.[1] || !isValidGaisukeWeekStart(m[1])) return null;
  return m[1];
}

export function getLocaleSwitchPublicPage(pathname: string): string | null {
  if (isKeibaArenaPublicPath(pathname)) return 'arena/keiba';
  if (isBanquetPublicPath(pathname)) return 'arena/banquet';
  if (isEconReportPublicPath(pathname)) return 'arena/econ';
  if (isGaisukeFxPublicPath(pathname)) return 'arena/fx';
  if (isGaisukePublicPath(pathname)) return 'gaisuke';
  for (const page of LOCALE_SWITCH_PUBLIC_PAGES) {
    if (LOCALE_SWITCH_CUSTOM_PAGES.includes(page)) continue;
    if (isLocalePrefixedPage(pathname, page)) return page;
  }
  return null;
}

/**
 * 言語切替後の遷移先。
 * 週次サマリーにいるときは weekStart を維持して /{locale}/gaisuke/weekly/{week} へ。
 */
export function getLocaleSwitchNavigatePath(pathname: string, nextLocale: string): string | null {
  const weekStart = parseGaisukeWeeklyWeekStartFromPath(pathname);
  if (weekStart) {
    return gaisukeWeeklySummaryPath(weekStart, nextLocale);
  }
  if (isGaisukeFaqPath(pathname)) {
    return gaisukeFaqPath(nextLocale);
  }
  // 記事を読んでいる途中で言語を変えたら、同じ記事の別言語へ移る
  const econSlug = parseEconReportSlugFromPath(pathname);
  if (econSlug) {
    return econReportDetailPath(nextLocale, econSlug);
  }
  const publicPage = getLocaleSwitchPublicPage(pathname);
  if (!publicPage) return null;
  return localePrefixedPath(nextLocale, publicPage);
}
